import axios from "../axios";

export async function getDeviationPdf(payload) {
  try {
    const response = await axios.get(`/quality/deviations/${payload.id}/pdf`, {
      params: payload.params,
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    const blob = await requirePdfBlob(response.data);
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const filename = match
      ? decodeURIComponent(match[1].replace(/"/g, ""))
      : "avvik.pdf";
    return { blob, filename };
  } catch (error) {
    if (error?.code === "DEVIATION_PDF_INVALID") throw error;
    const data = error?.response?.data;
    if (typeof Blob !== "undefined" && data instanceof Blob) {
      await requirePdfBlob(data);
    }
    throw error.response?.data || error;
  }
}

export async function fetchDeviations(controller) {
  return axios({
    method: "get",
    url: "/quality/deviations",
    params: controller,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchDeviation(id, params = {}) {
  return axios.get(`/quality/deviations/` + id, { params });
}

export async function fetchPublicDeviation(token) {
  return axios.get(`/quality/deviations/public/${token}`);
}

export async function createDeviation(payload) {
  return axios
    .post(`/quality/deviations`, { deviation: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function createDeviationItem(payload) {
  return axios
    .post(`/quality/deviations/${payload.deviation_id}/items`, {
      deviation_item: payload,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function deleteDeviationItem(payload) {
  return axios
    .delete(`/quality/deviations/${payload.deviation_id}/items/${payload.id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function updateDeviationItem(payload) {
  return axios
    .put(`/quality/deviations/${payload.deviation_id}/items/${payload.id}`, {
      deviation_item: payload,
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function updateDeviation(payload) {
  return axios
    .put(`/quality/deviations/${payload.id}`, { deviation: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

/** Create + link a work order for “Utfør arbeid” (today / tomorrow / custom date). */
export async function performDeviationWork(deviationId, payload = {}) {
  return axios
    .post(`/quality/deviations/${deviationId}/perform_work`, payload)
    .then((response) => response.data);
}

export async function printDeviation(payload) {
  return axios.put(`/quality/deviations/${payload.id}/print`, {
    deviation: payload,
  });
}

export async function sendDeviation(payload) {
  return axios
    .put(`/quality/deviations/${payload.id}/send`, {
      deviation: payload,
    })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || error));
}

export async function fetchDeviationDeletionDecision(id) {
  return axios
    .get(`/quality/deviations/${id}/deletion_decision`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function closeDeviationWithoutCompletion(id) {
  return axios
    .post(`/quality/deviations/${id}/close_without_completion`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function deleteDeviationById(id) {
  return axios
    .delete(`/quality/deviations/${id}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function attachDeviationDrawingCapture(id, payload = {}) {
  return axios
    .post(`/quality/deviations/${id}/drawing_capture`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function deleteDeviation(payload) {
  const deviation = payload.deviation;
  return axios
    .delete(`/quality/deviations/${deviation.deviation_id}`, {
      data: JSON.stringify({
        deviation,
      }),
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

function readBlobAsText(blob) {
  if (typeof blob?.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function requirePdfBlob(data) {
  if (typeof Blob !== "undefined" && !(data instanceof Blob)) {
    const err = new Error("Invalid pdf");
    err.code = "DEVIATION_PDF_INVALID";
    throw err;
  }
  const header = await readBlobAsText(data.slice(0, 4));
  if (header !== "%PDF") {
    let message = "Invalid pdf";
    try {
      const json = JSON.parse(await readBlobAsText(data));
      if (Array.isArray(json.errors) && json.errors.length) {
        message = json.errors.join(", ");
      }
    } catch (_) {
      /* HTML or empty error body */
    }
    const err = new Error(message);
    err.code = "DEVIATION_PDF_INVALID";
    throw err;
  }
  return data;
}
